import { Box, Flex, FlexProps, Text } from '@chakra-ui/react'
import { KeyboardEvent, useRef } from 'react'

import { Icon } from '../Icon'

/**
 * A nota que quer dizer "esta pergunta não se aplica a mim".
 *
 * Era o número `-1` digitado nos seis desenhos do componente e nos botões que o ligam, sem nome
 * nenhum — a mesma constante mágica em dez lugares.
 */
export const NOT_APPLICABLE = -1

/** Quantas estrelas a escala tem. Cinco em todos os formulários do painel. */
const DEFAULT_MAX = 5

export interface IStarRatingProps extends Omit<FlexProps, 'onChange'> {
  /** A nota, de 1 a `max`. `NOT_APPLICABLE` mostra a frase no lugar das estrelas. */
  value: number
  /** Sem ele o componente é leitura: as estrelas deixam de ser botões. */
  onChange?: (value: number) => void
  /** Tamanho da escala. Padrão 5. */
  max?: number
  /** Rótulo acessível do grupo — normalmente o enunciado da pergunta. */
  label?: string
}

/**
 * Escala de estrelas dos formulários Magenta (Acelera, Brilha, Conexão).
 *
 * Estava definida localmente em **seis arquivos**, byte a byte igual: só muda ter ou não
 * `onChange`, e por isso duas das telas de leitura passavam um `onChange={() => {}}` com um
 * `eslint-disable` em cima para desligar o que não queriam. Aqui a leitura é a ausência do
 * `onChange`, que é o que ela sempre foi.
 *
 * **A acessibilidade é o motivo de o componente existir, e não só a repetição.** Nas seis cópias
 * a estrela era um `<i>` com `onClick`: sem foco, sem teclado, sem papel e sem rótulo — o
 * formulário público de avaliação não era operável por teclado. Aqui cada estrela é um
 * `<button>` dentro de um `role="radiogroup"`, com foco visível, seleção pelas setas e
 * `tabIndex` móvel (só a estrela marcada entra na ordem de tabulação, que é o padrão de um grupo
 * de rádio). Em leitura não há botão nenhum: o grupo vira uma imagem com o texto da nota.
 *
 * O `#656565` do "Não se aplica" era o único hex fora do tema no bloco, e sai junto.
 */
export default function StarRating({ value, onChange, max = DEFAULT_MAX, label, ...rest }: IStarRatingProps) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const stars = Array.from({ length: max }, (_, index) => index + 1)
  const isReadOnly = !onChange
  // `parseInt('')` é `NaN` e o banco pode devolver nota fora da escala: sem o saneamento, nenhuma
  // estrela casaria com o `value` e o grupo inteiro ficaria com `tabIndex={-1}`, fora do Tab.
  const rating = Number.isFinite(value) ? Math.min(Math.max(value, 0), max) : 0

  const select = (star: number) => {
    onChange?.(star)
    buttons.current[star - 1]?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = ['ArrowRight', 'ArrowUp'].includes(event.key)
      ? 1
      : ['ArrowLeft', 'ArrowDown'].includes(event.key)
        ? -1
        : 0

    if (!step) return

    event.preventDefault()
    select(Math.min(max, Math.max(1, rating + step)))
  }

  if (value === NOT_APPLICABLE) {
    return (
      // O contêiner é o mesmo do grupo de estrelas, e não um `<Text>` solto: é ele que recebe as
      // props de layout do call site nos dois estados.
      <Flex align="center" {...rest}>
        <Text as="i" color="gray.500">
          Não se aplica
        </Text>
      </Flex>
    )
  }

  return (
    <Flex
      align="center"
      gap={1}
      fontSize="1.4rem"
      lineHeight="1"
      role={isReadOnly ? 'img' : 'radiogroup'}
      aria-label={isReadOnly ? `${rating} de ${max} estrelas` : label}
      onKeyDown={isReadOnly ? undefined : handleKeyDown}
      {...rest}
    >
      {stars.map((star) => {
        const isFilled = star <= rating
        const glyph = <Icon name={isFilled ? 'lni lni-star-fill' : 'lni lni-star-empty'} aria-hidden />

        if (isReadOnly) {
          return (
            <Box key={star} color={isFilled ? 'magenta.500' : undefined}>
              {glyph}
            </Box>
          )
        }

        return (
          <Box
            key={star}
            as="button"
            type="button"
            ref={(element: HTMLButtonElement | null) => {
              buttons.current[star - 1] = element
            }}
            role="radio"
            aria-checked={star === rating}
            // A nota é a estrela, não o glifo: "3 estrelas" é o que o leitor de tela anuncia.
            aria-label={`${star} ${star === 1 ? 'estrela' : 'estrelas'}`}
            // `tabIndex` móvel: o grupo inteiro é uma parada de tabulação, e as setas andam
            // dentro dele. Sem nota ainda, a primeira estrela é a porta de entrada.
            tabIndex={star === rating || (rating <= 0 && star === 1) ? 0 : -1}
            color={isFilled ? 'magenta.500' : undefined}
            lineHeight="1"
            borderRadius="sm"
            _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
            onClick={() => select(star)}
          >
            {glyph}
          </Box>
        )
      })}
    </Flex>
  )
}
